import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
    cn,
} from '@repo/shadcn';
import { FieldValues, Path, useFormContext } from 'react-hook-form';

type SelectFilterProps<T extends FieldValues> = {
    options?: Array<{ label: string; value: string }>;
    title?: string;
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    description?: string;
    label?: string;
    name: Path<T>;
};

export const SelectField = <T extends FieldValues>({
    options,
    title,
    placeholder,
    className = '',
    name,
    description = undefined,
    label = undefined,
}: SelectFilterProps<T>) => {
    const { control } = useFormContext<T>();

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    {label && <FormLabel>{label}</FormLabel>}
                    <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        value={field.value}
                    >
                        <FormControl>
                            <SelectTrigger
                                className={cn(
                                    'w-fit min-w-[150px] border border-gray-300 bg-white text-[14px] focus-visible:ring-1',
                                    className,
                                )}
                            >
                                <SelectValue
                                    placeholder={
                                        placeholder || `Select ${title}`
                                    }
                                    className="text-black/75"
                                />
                            </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                            <SelectGroup>
                                {title && <SelectLabel>{title}</SelectLabel>}
                                {options?.map((option) => (
                                    <SelectItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    {description && (
                        <FormDescription>{description}</FormDescription>
                    )}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
};

SelectField.displayName = 'SelectField';
